import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CloseOutlined, SendOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useTranslate } from '@/translations';
import type {
  MediaSendProgress,
  MediaSendProgressHandler,
} from '../runtime/contracts';
import { ComposerActivityTray } from './ComposerActivityTray';

const supportedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);

type PendingImagePhase = 'staged' | MediaSendProgress['phase'] | 'failed';

const isMediaSendPhase = (
  phase: PendingImagePhase,
): phase is MediaSendProgress['phase'] =>
  phase !== 'staged' && phase !== 'failed';

interface PendingImage {
  id: string;
  file: File;
  previewUrl: string;
  phase: PendingImagePhase;
  loaded?: number;
  total?: number;
}

interface ImageComposerActions {
  openPicker(): void;
  activity: ReactNode;
}

interface ImageComposerProps {
  children(actions: ImageComposerActions): ReactNode;
  disabled?: boolean;
  blockedByVoice?: boolean;
  onActivityChange?(active: boolean): void;
  onSend(file: File, onProgress: MediaSendProgressHandler): Promise<void>;
}

export const ImageComposer = ({
  children,
  disabled = false,
  blockedByVoice = false,
  onActivityChange,
  onSend,
}: ImageComposerProps) => {
  const { t } = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(0);
  const pendingRef = useRef<PendingImage[]>([]);
  const [pending, setPending] = useState<PendingImage[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [errorKey, setErrorKey] = useState<
    'commsUnsupportedImage' | 'commsFinishVoiceBeforeImages' | null
  >(null);

  const phaseLabel = (phase: MediaSendProgress['phase']) => {
    switch (phase) {
      case 'processing':
        return t('commsMediaPhase.processing');
      case 'uploading-original':
        return t('commsMediaPhase.uploading-original');
      case 'uploading-thumbnail':
        return t('commsMediaPhase.uploading-thumbnail');
      case 'uploading-voice':
        return t('commsMediaPhase.uploading-voice');
      case 'sending':
        return t('commsMediaPhase.sending');
    }
  };

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => {
    onActivityChange?.(pending.length > 0);
  }, [onActivityChange, pending.length]);

  useEffect(
    () => () => {
      pendingRef.current.forEach((item) =>
        URL.revokeObjectURL(item.previewUrl),
      );
    },
    [],
  );

  const stageFiles = useCallback(
    (files: File[] | FileList) => {
      if (blockedByVoice) {
        setErrorKey('commsFinishVoiceBeforeImages');
        return;
      }
      if (disabled) return;
      const candidates = Array.from(files);
      const images = candidates.filter((file) =>
        supportedImageTypes.has(file.type),
      );
      setErrorKey(
        images.length === candidates.length ? null : 'commsUnsupportedImage',
      );
      if (images.length === 0) return;
      setPending((current) => [
        ...current,
        ...images.map((file) => ({
          id: `image-${nextId.current++}`,
          file,
          previewUrl: URL.createObjectURL(file),
          phase: 'staged' as const,
        })),
      ]);
    },
    [blockedByVoice, disabled],
  );

  const remove = useCallback((id: string) => {
    setPending((current) => {
      const item = current.find((candidate) => candidate.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return current.filter((candidate) => candidate.id !== id);
    });
  }, []);

  const removeAll = useCallback(() => {
    setPending((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  const update = useCallback((id: string, progress: MediaSendProgress) => {
    setPending((current) =>
      current.map((item) =>
        item.id === id
          ? {
              ...item,
              phase: progress.phase,
              loaded: progress.loaded,
              total: progress.total,
            }
          : item,
      ),
    );
  }, []);

  const send = useCallback(
    async (item: PendingImage) => {
      update(item.id, { phase: 'processing' });
      try {
        await onSend(item.file, (progress) => update(item.id, progress));
        remove(item.id);
      } catch {
        setPending((current) =>
          current.map((candidate) =>
            candidate.id === item.id
              ? { ...candidate, phase: 'failed' }
              : candidate,
          ),
        );
      }
    },
    [onSend, remove, update],
  );

  const sendPending = () => {
    pendingRef.current
      .filter((item) => item.phase === 'staged' || item.phase === 'failed')
      .forEach((item) => void send(item));
  };

  const openPicker = () => {
    if (!disabled && !blockedByVoice) inputRef.current?.click();
  };

  const displayedImages = pending.slice(0, pending.length > 2 ? 2 : 3);
  const remainingCount = Math.max(0, pending.length - displayedImages.length);
  const totalBytes = pending.reduce((total, item) => total + item.file.size, 0);
  const sizeLabel =
    totalBytes >= 1024 * 1024
      ? `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`
      : totalBytes >= 1024
        ? `${(totalBytes / 1024).toFixed(1)} KB`
        : `${totalBytes} B`;
  const progressValues = pending
    .filter((item) => item.loaded !== undefined && item.total)
    .map((item) => (item.loaded! / item.total!) * 100);
  const aggregateProgress =
    progressValues.length > 0
      ? Math.round(
          progressValues.reduce((total, value) => total + value, 0) /
            pending.length,
        )
      : 0;
  const failed = pending.some((item) => item.phase === 'failed');
  const activePhase = pending.map((item) => item.phase).find(isMediaSendPhase);
  const hasSendable = pending.some(
    (item) => item.phase === 'staged' || item.phase === 'failed',
  );

  const activity =
    pending.length > 0 ? (
      <ComposerActivityTray
        kind="images"
        phase={failed ? 'failed' : (activePhase ?? 'staged')}
      >
        <div className="command-centre-chat__image-previews">
          {displayedImages.map((item) => (
            <img
              key={item.id}
              src={item.previewUrl}
              alt={item.file.name}
              title={item.file.name}
            />
          ))}
          {remainingCount > 0 && (
            <span className="command-centre-chat__image-count">
              +{remainingCount}
            </span>
          )}
        </div>
        <div className="command-centre-chat__image-summary">
          <span role={failed ? 'alert' : undefined}>
            {failed
              ? t('commsImageSendFailed')
              : activePhase
                ? phaseLabel(activePhase)
                : t('commsImagesReady', { count: pending.length })}
          </span>
          <small>{sizeLabel}</small>
          <progress
            aria-label={t('commsImageUploadProgress')}
            max={100}
            value={aggregateProgress}
          />
        </div>
        <div className="command-centre-chat__activity-actions">
          <Button
            type="text"
            icon={<CloseOutlined />}
            className="command-centre-chat__activity-action"
            onClick={removeAll}
            aria-label={t('commsRemoveAllImages')}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            className="command-centre-chat__activity-action command-centre-chat__activity-action--primary"
            onClick={sendPending}
            disabled={disabled || blockedByVoice || !hasSendable}
            aria-label={t('commsSendImages')}
          />
        </div>
      </ComposerActivityTray>
    ) : null;

  return (
    <div
      className="command-centre-chat__image-composer"
      data-testid="comms-image-drop-zone"
      onPaste={(event) => {
        const files = Array.from(event.clipboardData.items)
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFile())
          .filter((file): file is File => Boolean(file));
        if (files.length === 0) return;
        event.preventDefault();
        stageFiles(files);
      }}
      onDragOver={(event) => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return;
        event.preventDefault();
        setDragOver(!blockedByVoice && !disabled);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        if (event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        setDragOver(false);
        stageFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        hidden
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => {
          if (event.target.files) stageFiles(event.target.files);
          event.target.value = '';
        }}
      />
      {children({ openPicker, activity })}
      {errorKey && <span role="alert">{t(errorKey)}</span>}
      {dragOver && (
        <div className="command-centre-chat__drop-overlay">
          {t('commsDropImages')}
        </div>
      )}
    </div>
  );
};
